/**
 * Preview support for "section" variables (notes, notices, messages, buttons).
 *
 * The backend fills these with a table row (<tr>…</tr>) or cell (<td>…</td>) and places
 * them with the same rule as below (SDK: utils/EmailTemplateRowExpander.placeSectionVariables).
 * Keep both copies in sync so the Preview matches the real email.
 */

// Row-shaped sections (backend value starts with <tr>).
const ROW_SECTION_LABELS: Record<string, string> = {
  mandatory_notice_section: 'Mandatory notice will appear here',
  notes_section: 'Admin notes will appear here',
  custom_message_section: 'Admin message will appear here',
  mandatory_deadline_section: 'Mandatory / deadline badges will appear here',
  mandatory_section: 'Mandatory / deadline badges will appear here',
  view_results_section: 'View Full Results button will appear here',
};

// Cell-shaped sections (backend value starts with <td>) — buttons meant to sit side by side.
const CELL_SECTION_LABELS: Record<string, string> = {
  certificate_button: 'Download Certificate',
  certificate_button_section: 'Download Certificate',
  view_results_button_section: 'View Results',
};

// Sample box for each section variable used in the template (preview only).
export function buildSectionSamples(variable_names: string[]): Record<string, string> {
  const samples: Record<string, string> = {};
  for (const name of variable_names) {
    const row_label = ROW_SECTION_LABELS[name];
    const cell_label = CELL_SECTION_LABELS[name];
    if (row_label) {
      samples[name] =
        `<tr><td style="padding-top: 20px;">` +
        `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #fff7ed; border-left: 4px solid #f97316; border-radius: 4px;">` +
        `<tr><td style="padding: 12px 15px; color: #9a3412; font-size: 14px; font-family: Arial, Helvetica, sans-serif;">${row_label}</td></tr>` +
        `</table></td></tr>`;
    } else if (cell_label) {
      samples[name] =
        `<td style="padding: 0 6px;">` +
        `<table cellpadding="0" cellspacing="0" border="0"><tr>` +
        `<td style="background-color: #f97316; padding: 12px 24px; text-align: center; border-radius: 6px; color: #ffffff; font-weight: bold; font-size: 14px; font-family: Arial, Helvetica, sans-serif;">${cell_label}</td>` +
        `</tr></table></td>`;
    }
  }
  return samples;
}

// Section variables are a table row (<tr>) or cell (<td>) and fit only in that table slot.
// Anywhere else (inside text or a heading) insert just the inner box, without
// moving the text around it or dropping its style.
export function placeSectionVariables(html: string, variables: Record<string, string>): string {
  if (!html || !variables) return html;

  let result = html;
  for (const [key, value] of Object.entries(variables)) {
    // Numbers/booleans are never table HTML; the caller's own replace handles them.
    if (typeof value !== 'string') continue;
    const shape = sectionShape(value);
    if (!shape) continue;

    const inner = boxForText(shape, value);
    const fits_here = shape === 'row' || shape === 'rows' ? isBetweenRows : isBetweenCells;
    const token = new RegExp(`\\{\\{\\{?\\s*${escapeRegex(key)}\\s*\\}?\\}\\}`, 'g');
    result = result.replace(token, (match: string, offset: number, whole: string) => {
      if (fits_here(whole, offset, match.length)) return value;
      // Inside a <p>: close it before the box and reopen it after, so the text after keeps its style.
      const split = paragraphSplit(whole, offset);
      return split ? `${split.close}${BOX_START}${inner}${BOX_END}${split.reopen}` : inner;
    });
  }
  return tidyParagraphSplits(result);
}

const BOX_START = '\u0000BOX_START\u0000';
const BOX_END = '\u0000BOX_END\u0000';
const INLINE_TAGS = 'a|abbr|b|big|cite|code|em|font|i|mark|s|small|span|strike|strong|sub|sup|u';
const INLINE_TAG_SET = new Set(INLINE_TAGS.split('|'));
// Tags that make the browser close an open <p> on their own.
const P_CLOSING_BLOCK = /<(address|article|aside|blockquote|details|dialog|div|dl|dd|dt|fieldset|figcaption|figure|footer|form|h[1-6]|header|hgroup|hr|li|main|menu|nav|ol|pre|section|table|ul)\b/i;

// The open <p> (plus inline tags like <span style>) around the placeholder, as close/reopen HTML.
// Null when the placeholder is not inside a still-open paragraph.
function paragraphSplit(html: string, offset: number): { close: string; reopen: string } | null {
  // Comments (e.g. Outlook <!--[if mso]>…) are blanked so a <p> inside them is never counted.
  const before = html.slice(0, offset).replace(/<!--[\s\S]*?-->/g, (c) => ' '.repeat(c.length));
  const p_open = /<p(?:\s[^>]*)?>/gi;
  let last: RegExpExecArray | null = null;
  let m: RegExpExecArray | null;
  while ((m = p_open.exec(before)) !== null) last = m;
  if (!last) return null;

  const inside = before.slice(last.index + last[0].length);
  if (/<\/p\s*>/i.test(inside) || P_CLOSING_BLOCK.test(inside)) return null;
  // A <p> never reaches into another table cell; one left open in an earlier cell is not ours.
  if (/<\/?(td|th|tr)\b/i.test(inside)) return null;

  const open_inline: Array<{ name: string; tag: string }> = [];
  const tag_re = /<(\/?)([a-zA-Z][\w-]*)\b[^>]*>/g;
  while ((m = tag_re.exec(inside)) !== null) {
    const name = m[2].toLowerCase();
    if (!INLINE_TAG_SET.has(name)) continue;
    if (m[1]) {
      const i = open_inline.map((t) => t.name).lastIndexOf(name);
      if (i >= 0) open_inline.splice(i, 1);
    } else {
      open_inline.push({ name, tag: m[0] });
    }
  }
  return {
    close: open_inline.slice().reverse().map((t) => `</${t.name}>`).join('') + '</p>',
    reopen: last[0] + open_inline.map((t) => t.tag).join(''),
  };
}

// Drop the empty paragraphs and the extra <br> a split can leave, then remove the markers.
function tidyParagraphSplits(html: string): string {
  if (!html.includes(BOX_START)) return html;
  return html
    .replace(new RegExp(`(${BOX_END}<p(?:\\s[^>]*)?>(?:<(?:${INLINE_TAGS})\\b[^>]*>)*)\\s*<br\\s*/?>`, 'gi'), '$1')
    .replace(new RegExp(`<p(?:\\s[^>]*)?>\\s*</p>${BOX_START}`, 'gi'), '')
    .replace(new RegExp(`${BOX_END}<p(?:\\s[^>]*)?>\\s*</p>`, 'gi'), '')
    .split(BOX_START).join('')
    .split(BOX_END).join('');
}

type SectionShape = 'row' | 'cell' | 'rows' | 'cells';

// row/cell = one single-cell box; rows/cells = a list of rows (e.g. items table) or several cells.
// Anything that is not purely table rows or cells is left as-is.
function sectionShape(value: string): SectionShape | null {
  if (!value) return null;
  const outer = stripNestedTables(value);
  const row_count = (outer.match(/<tr[\s>]/gi) || []).length;
  const cell_count = (outer.match(/<t[dh][\s>]/gi) || []).length;
  if (/^\s*<tr[\s>]/i.test(value) && isOnly(outer, /<tr\b[^>]*>[\s\S]*?<\/tr>/gi)) {
    return row_count === 1 && cell_count === 1 ? 'row' : 'rows';
  }
  if (/^\s*<td[\s>]/i.test(value) && row_count === 0 && isOnly(outer, /<t[dh]\b[^>]*>[\s\S]*?<\/t[dh]>/gi)) {
    return cell_count === 1 ? 'cell' : 'cells';
  }
  return null;
}

// True when html is nothing but repeats of the given block (plus whitespace).
function isOnly(html: string, block: RegExp): boolean {
  return html.replace(block, '').trim() === '';
}

// What to insert when the value is placed in text instead of its table slot.
// Lists of rows/cells get their own table so they render as a proper mini table.
function boxForText(shape: SectionShape, value: string): string {
  const table_open = '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">';
  if (shape === 'row') return unwrapRow(value);
  if (shape === 'cell') return unwrapCell(value);
  if (shape === 'rows') return `${table_open}${value}</table>`;
  return `${table_open}<tr>${value}</tr></table>`;
}

// Removes inner <table>…</table> blocks so only the outer rows/cells are counted.
function stripNestedTables(html: string): string {
  const inner_table = /<table\b[^>]*>(?:(?!<table\b)[\s\S])*?<\/table>/gi;
  let prev: string;
  let out = html;
  do {
    prev = out;
    out = out.replace(inner_table, '');
  } while (out !== prev);
  return out;
}

// True when the placeholder sits between rows, e.g. </tr>{{x}}<tr>.
// Neighbouring placeholders, HTML comments and whitespace are skipped.
function isBetweenRows(html: string, offset: number, length: number): boolean {
  const before = trimPlaceholders(html.slice(Math.max(0, offset - 2000), offset), 'end');
  const after = trimPlaceholders(html.slice(offset + length, offset + length + 2000), 'start');
  return /(<\/tr>|<\/thead>|<\/tbody>|<tbody[^>]*>|<table[^>]*>)$/i.test(before)
    && /^(<tr[\s>]|<\/tbody>|<\/table>|<tbody[\s>]|<tfoot[\s>])/i.test(after);
}

// True when the placeholder sits among a row's cells, e.g. </td>{{x}}</tr>.
function isBetweenCells(html: string, offset: number, length: number): boolean {
  const before = trimPlaceholders(html.slice(Math.max(0, offset - 2000), offset), 'end');
  const after = trimPlaceholders(html.slice(offset + length, offset + length + 2000), 'start');
  return /(<\/td>|<\/th>|<tr(\s[^>]*)?>)$/i.test(before)
    && /^(<td[\s>]|<th[\s>]|<\/tr>)/i.test(after);
}

function trimPlaceholders(s: string, side: 'start' | 'end'): string {
  const edge = side === 'end'
    ? /(\{\{\{?[^{}]+\}?\}\}|<!--(?:(?!<!--)[\s\S])*?-->)$/
    : /^(\{\{\{?[^{}]+\}?\}\}|<!--[\s\S]*?-->)/;
  let prev: string;
  do {
    prev = s;
    s = (side === 'end' ? s.trimEnd() : s.trimStart()).replace(edge, '');
  } while (s !== prev);
  return s;
}

// <tr><td style="S">X</td></tr>  →  <div style="S">X</div>  (keeps the spacing)
function unwrapRow(value: string): string {
  const m = value.match(/^\s*<tr[^>]*>\s*<td([^>]*)>([\s\S]*)<\/td>\s*<\/tr>\s*$/i);
  return m ? cellToDiv(m[1], m[2]) : value;
}

// <td style="S">X</td>  →  <div style="S">X</div>
function unwrapCell(value: string): string {
  const m = value.match(/^\s*<td([^>]*)>([\s\S]*)<\/td>\s*$/i);
  return m ? cellToDiv(m[1], m[2]) : value;
}

function cellToDiv(td_attributes: string, content: string): string {
  const style = (td_attributes.match(/style\s*=\s*"([^"]*)"/i) || [])[1];
  return style ? `<div style="${style}">${content}</div>` : content;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
